
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { activityFeedService } from '@/services/activity';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Clock } from 'lucide-react';
import ActivityIcon from '@/components/activity/ActivityIcon';
import { formatDistanceToNow } from 'date-fns';

interface FamilyActivityWidgetProps {
  shouldShow: boolean;
}

const FamilyActivityWidget: React.FC<FamilyActivityWidgetProps> = ({ shouldShow }) => {
  const { familyId, hasFamily } = useCurrentUserFamily();

  const { data: response, isLoading } = useQuery({
    queryKey: ['onboarding-family-activities', familyId],
    queryFn: () => activityFeedService.getFamilyActivities(familyId!, 3, 0),
    enabled: shouldShow && !!familyId && hasFamily,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const activities = response?.data || [];

  const getActivityDescription = (activity: any) => {
    switch (activity.activity_type) {
      case 'recipe_created':
        return `${activity.user_first_name} created ${activity.entity_name}`;
      case 'recipe_updated':
        return `${activity.user_first_name} updated ${activity.entity_name}`;
      case 'meal_planned':
        return `${activity.user_first_name} planned ${activity.entity_name}`;
      case 'meal_updated':
        return `${activity.user_first_name} updated meal plan`;
      case 'feedback_added':
        return `${activity.user_first_name} reviewed ${activity.entity_name}`;
      default:
        return `${activity.user_first_name} performed an activity`;
    }
  };

  if (!shouldShow) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="border-sage/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-sage" />
            </div>
            <CardTitle className="text-lg text-foreground">Recent Family Activity</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-6 w-6 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return null;
  }

  return (
    <Card className="border-sage/20 bg-gradient-to-br from-sage/5 to-transparent">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
            <Users className="h-5 w-5 text-sage" />
          </div>
          <CardTitle className="text-lg text-foreground">Recent Family Activity</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-3 p-3 bg-white/50 rounded-lg border border-sage/10">
              <ActivityIcon activityType={activity.activity_type} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">
                  {getActivityDescription(activity)}
                </p>
                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          Join your family's meal planning journey!
        </p>
      </CardContent>
    </Card>
  );
};

export default FamilyActivityWidget;
